import React from 'react';
import {
  Box,
  Typography,
  Slider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';

interface WidgetLayoutFormProps {
  layout: {
    width: number;
    height: number;
    order: number;
  };
  onLayoutChange: (layout: {
    width: number;
    height: number;
    order: number;
  }) => void;
  existingWidgets: any[];
}

export const WidgetLayoutForm: React.FC<WidgetLayoutFormProps> = ({
  layout,
  onLayoutChange,
  existingWidgets,
}) => {
  const handleWidthChange = (_: Event, value: number | number[]) => {
    onLayoutChange({
      ...layout,
      width: Array.isArray(value) ? value[0] : value,
    });
  };

  const handleHeightChange = (_: Event, value: number | number[]) => {
    onLayoutChange({
      ...layout,
      height: Array.isArray(value) ? value[0] : value,
    });
  };

  const handleOrderChange = (order: number) => {
    onLayoutChange({
      ...layout,
      order,
    });
  };

  return (
    <Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Widget Layout
      </Typography>

      <Box sx={{ mb: 2 }}>
        <Typography variant="caption" sx={{ mb: 1, display: 'block' }}>
          Preview (Grid: {layout.width} × {layout.height})
        </Typography>
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            height: 120,
            border: '2px solid #e0e0e0',
            borderRadius: 1,
            bgcolor: '#f5f5f5',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              border: '2px solid',
              borderColor: 'primary.main',
              borderRadius: 1,
              width: `${(layout.width / 12) * 100}%`,
              height: `${(layout.height / 8) * 100}%`,
              transition: 'all 0.2s',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: 8,
              right: 8,
              fontSize: '0.75rem',
              color: 'text.secondary',
              bgcolor: 'white',
              px: 1,
              borderRadius: 1,
            }}
          >
            <Typography variant="caption" color="text.secondary">
              {layout.width}w × {layout.height}h
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box sx={{ mb: 2 }}>
        <Typography variant="caption">
          Width (Grid Columns: {layout.width})
        </Typography>
        <Slider
          value={[layout.width]}
          onChange={handleWidthChange}
          max={12}
          min={1}
          marks
          valueLabelDisplay="auto"
        />
      </Box>

      <Box sx={{ mb: 2 }}>
        <Typography variant="caption">
          Height (Grid Rows: {layout.height})
        </Typography>
        <Slider
          value={[layout.height]}
          onChange={handleHeightChange}
          max={8}
          min={1}
          marks
          valueLabelDisplay="auto"
        />
      </Box>

      <FormControl fullWidth>
        <InputLabel>Widget Order</InputLabel>
        <Select
          value={layout.order.toString()}
          label="Widget Order"
          onChange={(e) => handleOrderChange(parseInt(e.target.value, 10))}
        >
          {Array.from(
            { length: Math.max(10, existingWidgets.length + 5) },
            (_, index) => index + 1
          ).map((orderValue) => (
            <MenuItem key={orderValue} value={orderValue.toString()}>
              {orderValue}
              {existingWidgets.some(
                (widget) => widget.layout?.order === orderValue
              ) && ' (occupied)'}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
};
